import type { FC } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { PapiGallection } from '@wix/da-papi-types';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import { ResourceType } from '@wix/da-shared-react/pkg/types/resource';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { PlaygroundSubsection, StudioSection } from '../../types/store';
import {
  getIsDiscountsSection,
  getIsEarningsSection,
  getIsOrdersSection,
  getIsProductsSection,
  getIsShopReportsSection,
  getNewModalLoading,
  getPlaygroundSubsection,
} from '../../selectors/studio';
import StudioHeader from '../../components/StudioHeader';
import StudioBlankSlate from '../../components/StudioBlankSlate';
import StudioNavigationDesktop from '../../components/StudioNavigationDesktop';
import StudioTableMobile from '../../components/StudioTableMobile';
import StudioTableDesktop from '../../components/StudioTableDesktop';
import StudioUploadsList from '../../components/StudioUploadsList';
import ProductsLobbyPageContent from '../../components/products/ProductsLobbyPageContent';
import ProductsSectionPageContent from '../../components/products/ProductsSectionPageContent';
import StudioHomePageContent from '../../components/home/StudioHomePageContent';
import ShopReportsPageContent from '../../components/reports/ShopReportsPageContent';
import PublishedPageContent from '../../components/published/PublishedPageContent';
import StudioFolderHeaderDesktop from '../../components/StudioFolderHeader/StudioFolderHeaderDesktop';
import { STUDIO_DEVIATIONS_STREAM } from '../../../constants';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import DeviationDuperbrowseContext from '@wix/da-shared-react/pkg/Duperbrowse/DeviationDuperbrowseContext';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// stash.duperbrowse.backto.*
import SellerDiscountsAll from '../../components/shop/SellerDiscounts/SellerDiscountsAll';
import ShopPageContentMobile from '../../components/shop/ShopPageContentMobile';
import EarningsPageContent from '../../components/earnings/EarningsPageContent';
import OrdersByOriginalPageContent from '../../components/orders/OrdersByOriginalPageContent';
import OrdersByStatusPageContent from '../../components/orders/OrdersByStatusPageContent';
import VideoCreator from '../VideoCreator';
import VideoCreatorMobile from '../VideoCreator/VideoCreatorMobile';

import s from './Studio.scss';

const backToSectioni18n = {
  [StudioSection.STASH]: 'stash.duperbrowse.backto.STASH',
  [StudioSection.DRAFTS]: 'stash.duperbrowse.backto.DRAFTS',
  [StudioSection.JOURNALS]: 'stash.duperbrowse.backto.JOURNALS',
  [StudioSection.LITERATURE]: 'stash.duperbrowse.backto.LITERATURE',
  [StudioSection.PLAYGROUND]: 'stash.duperbrowse.backto.PLAYGROUND',
  [StudioSection.PUBLISHED]: 'stash.duperbrowse.backto.PUBLISHED',
  [StudioSection.PUBLISHED_GALLERIES]: 'stash.duperbrowse.backto.PUBLISHED',
  [StudioSection.PUBLISHED_TIERS]: 'stash.duperbrowse.backto.PUBLISHED',
};

function useIsVideoSection(activeSection: StudioSection): boolean {
  const activePlaygroundSection = useSelector(getPlaygroundSubsection);
  return useMemo(
    () =>
      activeSection === StudioSection.PLAYGROUND &&
      activePlaygroundSection === PlaygroundSubsection.Video,
    [activeSection, activePlaygroundSection]
  );
}

export interface Props {
  activeSection: StudioSection;
  selectedFolder?: PapiGallection;
  subfolders?: PapiGallection[];
  hasDeviations?: boolean;
  isMobile?: boolean;
  duperbrowseStreamVersion?: number;
}

const DesktopContentArea: FC<Pick<Props, 'activeSection' | 'hasDeviations'>> = ({
  activeSection,
  hasDeviations,
}) => {
  const isVideoSection = useIsVideoSection(activeSection);
  const isProductsSection = useSelector(getIsProductsSection);
  const isStashPage = activeSection === StudioSection.STASH;
  const isPublishedPage = [
    StudioSection.PUBLISHED,
    StudioSection.PUBLISHED_GALLERIES,
    StudioSection.PUBLISHED_TIERS,
  ].includes(activeSection);
  const isHomePage = activeSection === StudioSection.HOME;
  const isProductsLobby = activeSection === StudioSection.PRODUCTS_LOBBY;
  const isDiscountSection = useSelector(getIsDiscountsSection);
  const isShopReportsSection = useSelector(getIsShopReportsSection);
  const isEarningsSection = activeSection === StudioSection.EARNINGS;
  const isOrdersStatusSection = activeSection === StudioSection.ORDERS_STATUS;
  const isOrdersOriginalsSection = activeSection === StudioSection.ORDERS_ORIGINALS;

  const showFolderHeader = isStashPage;

  if (isProductsLobby) {
    return <ProductsLobbyPageContent />;
  } else if (isProductsSection) {
    return <ProductsSectionPageContent activeSection={activeSection} />;
  }

  if (isHomePage) {
    return <StudioHomePageContent />;
  }

  if (isPublishedPage) {
    return <PublishedPageContent />;
  }

  if (isDiscountSection) {
    return <SellerDiscountsAll />;
  }

  if (isShopReportsSection) {
    return <ShopReportsPageContent />;
  }

  if (isEarningsSection) {
    return <EarningsPageContent />;
  }

  if (isOrdersStatusSection) {
    return <OrdersByStatusPageContent />;
  } else if (isOrdersOriginalsSection) {
    return <OrdersByOriginalPageContent />;
  }

  return (
    <>
      {showFolderHeader && <StudioFolderHeaderDesktop activeSection={activeSection} />}
      {isVideoSection ? (
        <VideoCreator />
      ) : hasDeviations ? (
        <StudioTableDesktop />
      ) : (
        <StudioBlankSlate />
      )}
    </>
  );
};

const MobileContentArea: FC<Pick<Props, 'activeSection'>> = ({ activeSection }) => {
  const isVideoSection = useIsVideoSection(activeSection);
  const isDiscountsSection = useSelector(getIsDiscountsSection);
  const isShopReportsSection = useSelector(getIsShopReportsSection);
  const isEarningsSection = useSelector(getIsEarningsSection);
  const isOrdersSection = useSelector(getIsOrdersSection);
  const isProductsLobby = activeSection === StudioSection.PRODUCTS_LOBBY;

  if (
    isProductsLobby ||
    isShopReportsSection ||
    isDiscountsSection ||
    isEarningsSection ||
    isOrdersSection
  ) {
    return <ShopPageContentMobile />;
  }

  if (isVideoSection) {
    return <VideoCreatorMobile />;
  }

  return <StudioTableMobile />;
};

const Studio: FC<Props> = ({
  activeSection,
  selectedFolder,
  hasDeviations,
  isMobile,
  duperbrowseStreamVersion,
}) => {
  const { t } = useTranslation();
  const isDesktop = !isMobile;
  const newModalLoading = useSelector(getNewModalLoading);
  const isStashSection = activeSection === StudioSection.STASH;

  const { streamId, streamConfig, streamThrottle } = STUDIO_DEVIATIONS_STREAM;
  const { items: deviations } = useNormalizedDeviationStream(
    streamId,
    streamThrottle,
    streamConfig
  );

  // Use folder name if it isnt a special one, else use the preset
  const backText =
    selectedFolder &&
    !selectedFolder.isHidden &&
    !['sta.sh', 'sta.nk'].includes(selectedFolder.name)
      ? `Back to ${selectedFolder.name}`
      : t(backToSectioni18n[activeSection]);

  return (
    <DeviationDuperbrowseContext
      streamId={`studio-${deviations[0]?.deviationId ?? 0}-${duperbrowseStreamVersion}`}
      deviations={deviations}
      parent={{ title: backText }}
    >
      <BiLoggerContextProvider
        value={{
          page_typeid: isStashSection ? ResourceType.STASH_FOLDER : undefined,
          page_itemid: isStashSection ? selectedFolder?.folderId : undefined,
        }}
      >
        <div className={cn(s['root'])}>
          {newModalLoading && (
            <LoadingIndicator className={s['new-loader']} coverMode forSSR view="llama" />
          )}

          {isMobile && <MobileContentArea activeSection={activeSection} />}

          {isDesktop && (
            <>
              <div className={s['left-column']}>
                <StudioNavigationDesktop className={s['navigation']} />
              </div>
              <section className={s['table']}>
                <StudioHeader className={s['header']} />
                <DesktopContentArea activeSection={activeSection} hasDeviations={hasDeviations} />
              </section>
            </>
          )}

          <StudioUploadsList />
        </div>
      </BiLoggerContextProvider>
    </DeviationDuperbrowseContext>
  );
};
Studio.displayName = 'Studio';

export default Studio;
